'use client';

import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { categoryConfig, CategoryNode } from '../CategoryConfig';

interface ReviewStepProps {
  formData: {
    discipline: string | null;
    categories: string[];
    requirements: {
      [key: string]: {
        must: boolean;
        preferred: boolean;
      };
    };
    modelUrl?: string;
    modelFile?: File;
    jobDetails: {
      title: string;
      description: string;
      budget?: number;
      timeline?: string;
    };
  };
  onEditSection: (stepIndex: number) => void;
}

// Helper function to find a category node by ID
const findCategoryNode = (id: string, nodes: CategoryNode[]): CategoryNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findCategoryNode(id, node.children);
      if (found) return found;
    }
  }
  return null;
};

export default function ReviewStep({ formData, onEditSection }: ReviewStepProps) {
  const categories = formData.discipline
    ? categoryConfig[formData.discipline as keyof typeof categoryConfig]
    : [];

  const formatRequirements = (categoryId: string) => {
    const requirements = Object.entries(formData.requirements)
      .filter(([key]) => key.startsWith(categoryId))
      .map(([key, value]) => {
        const isCore = key === categoryId;
        const label = isCore
          ? 'Core Requirements'
          : key.replace(`${categoryId}-`, '').replace(/-/g, ' ');
        return {
          label: label.charAt(0).toUpperCase() + label.slice(1),
          must: value.must,
          preferred: value.preferred,
        };
      });

    return requirements;
  };

  return (
    <div>
      <Typography variant="h5" gutterBottom>
        Review Your Job
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Please review all the details of your job posting. You can edit any section by clicking the
        edit button.
      </Typography>

      <Grid container spacing={3}>
        {/* Discipline Section */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  Discipline
                </Typography>
                <Typography variant="body1">{formData.discipline}</Typography>
              </Box>
              <Button startIcon={<EditIcon />} onClick={() => onEditSection(1)} size="small">
                Edit
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Categories Section */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Categories
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.categories.map(categoryId => {
                    const category = findCategoryNode(categoryId, categories);
                    return category ? (
                      <Chip
                        key={categoryId}
                        label={category.label}
                        color="primary"
                        variant="outlined"
                      />
                    ) : null;
                  })}
                </Box>
              </Box>
              <Button startIcon={<EditIcon />} onClick={() => onEditSection(2)} size="small">
                Edit
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Requirements Section */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Requirements
                </Typography>
                {formData.categories.map((categoryId, index) => {
                  const category = findCategoryNode(categoryId, categories);
                  const requirements = formatRequirements(categoryId);

                  return category ? (
                    <Box key={categoryId} sx={{ mb: 2 }}>
                      {index > 0 && <Divider sx={{ my: 2 }} />}
                      <Typography variant="subtitle1" gutterBottom>
                        {category.label}
                      </Typography>
                      <List dense>
                        {requirements.map(req => (
                          <ListItem key={req.label}>
                            <ListItemText
                              primary={req.label}
                              secondaryTypographyProps={{ component: 'div' }}
                              secondary={
                                <Box sx={{ mt: 0.5 }}>
                                  {req.must && (
                                    <Chip
                                      size="small"
                                      label="Must Have"
                                      color="primary"
                                      sx={{ mr: 1 }}
                                    />
                                  )}
                                  {req.preferred && (
                                    <Chip size="small" label="Preferred" color="secondary" />
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  ) : null;
                })}
              </Box>
              <Button startIcon={<EditIcon />} onClick={() => onEditSection(3)} size="small">
                Edit
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Model Section */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  3D Model
                </Typography>
                {formData.modelFile ? (
                  <Typography variant="body1">{formData.modelFile.name}</Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No model uploaded
                  </Typography>
                )}
              </Box>
              <Button startIcon={<EditIcon />} onClick={() => onEditSection(4)} size="small">
                Edit
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Job Details Section */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Job Details
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  {formData.jobDetails.title}
                </Typography>
                <Typography variant="body1" paragraph>
                  {formData.jobDetails.description}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Budget:
                    </Typography>
                    <Typography variant="body1">
                      {formData.jobDetails.budget ? `£${formData.jobDetails.budget}` : 'Flexible'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Timeline:
                    </Typography>
                    <Typography variant="body1">
                      {formData.jobDetails.timeline || 'Flexible'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              <Button startIcon={<EditIcon />} onClick={() => onEditSection(5)} size="small">
                Edit
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
}
